/**
 * Clients view (`/clients`) — address-book parity with mobile.
 *
 * What this page shows: ONLY clients that exist in the explicit
 * agenda (the `clients` IndexedDB store, fed by mobile's
 * `saveClient` and the web's `broadcastClient`). It deliberately does
 * NOT enumerate every `Work.clientName` ever typed — that mismatch
 * with mobile's `ClientPickerModal` was the bug this view fixes.
 *
 * Read path:
 *   - `getClients()` — explicit `Client[]` from IndexedDB.
 *   - `getWorks(true)` — joined to surface "X trabajos · hace Y" per
 *     row. Active works only (the helper drops archived itself).
 *   - Live updates via `subscribeStore` — same pattern as Stats /
 *     WorkList. Both incoming `client_upsert` (mobile → web) and the
 *     local "Nuevo cliente" form trigger the emitter via the store.
 *
 * Create path:
 *   - "Nuevo cliente" button opens a modal form (name + phone +
 *     address + NIF/CIF). On submit we `broadcastClient(...)` so
 *     mobile peers see the new entry within the live-sync window
 *     (the relay queues for offline peers).
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Plus, Search, Users, X } from 'lucide-react';

import { EmptyState } from '../components/EmptyState';
import { SkeletonList } from '../components/Skeleton';
import { getClients, getWorks, subscribeStore } from '../store/db';
import { broadcastClient } from '../sync/syncClients';
import type { WebIdentity } from '../store/keys';
import type { Client, Work } from '../types';
import { buildAgendaGroups, type ClientGroup } from '../utils/clients';

import './Clients.css';

interface ClientsProps {
  identity: WebIdentity;
}

type SortKey = 'name' | 'recent' | 'count';

const SORT_OPTIONS: ReadonlyArray<{ key: SortKey; label: string }> = [
  { key: 'name', label: 'Nombre A–Z' },
  { key: 'recent', label: 'Más reciente' },
  { key: 'count', label: 'Más trabajos' },
];

function formatRelative(ts: number): string {
  const delta = Date.now() - ts;
  const day = 86_400_000;
  if (delta < day) return 'Hoy';
  if (delta < 2 * day) return 'Ayer';
  if (delta < 7 * day) return `Hace ${Math.floor(delta / day)} d`;
  if (delta < 30 * day) return `Hace ${Math.floor(delta / (7 * day))} sem`;
  return new Date(ts).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function normalizeQuery(q: string): string {
  return q
    .normalize('NFC')
    .toLocaleLowerCase('es')
    .replace(/[áàâä]/g, 'a')
    .replace(/[éèêë]/g, 'e')
    .replace(/[íìîï]/g, 'i')
    .replace(/[óòôö]/g, 'o')
    .replace(/[úùûü]/g, 'u')
    .replace(/ñ/g, 'n');
}

function newClientId(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  let hex = '';
  for (let i = 0; i < bytes.length; i++) hex += bytes[i].toString(16).padStart(2, '0');
  return hex;
}

export function Clients({ identity }: ClientsProps) {
  const [clients, setClients] = useState<Client[] | null>(null);
  const [works, setWorks] = useState<Work[] | null>(null);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortKey>('name');
  const [showForm, setShowForm] = useState(false);

  // Initial load + live re-load on store mutations (covers both works
  // edits and address-book mutations from `applyClientUpsert`).
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      void Promise.all([getClients(), getWorks(true)]).then(([cs, ws]) => {
        if (cancelled) return;
        setClients(cs);
        setWorks(ws);
      });
    };
    load();
    const unsub = subscribeStore(load);
    return () => {
      cancelled = true;
      unsub();
    };
  }, []);

  const groups: ClientGroup[] = useMemo(() => {
    if (!clients || !works) return [];
    return buildAgendaGroups(clients, works);
  }, [clients, works]);

  const filtered = useMemo(() => {
    if (!groups.length) return groups;
    let out = groups;
    const q = normalizeQuery(search.trim());
    if (q) {
      out = out.filter((g) => normalizeQuery(g.name).includes(q));
    }
    const sorted = [...out];
    sorted.sort((a, b) => {
      if (sort === 'name') return a.name.localeCompare(b.name, 'es');
      if (sort === 'recent') return b.lastUpdatedAt - a.lastUpdatedAt;
      return b.total - a.total;
    });
    return sorted;
  }, [groups, search, sort]);

  const isLoading = clients === null || works === null;

  return (
    <main id="main-content" className="cli-screen" data-testid="clients-screen">
      <header className="cli-header">
        <Link to="/" className="cli-back" aria-label="Volver" data-testid="clients-back">
          <ChevronLeft size={20} strokeWidth={2} aria-hidden="true" />
          Volver
        </Link>
        <div className="cli-header-row">
          <div className="cli-header-title">
            <Users size={24} strokeWidth={2} aria-hidden="true" />
            <h1 className="cli-h1">Clientes</h1>
          </div>
          <button
            type="button"
            className="cli-new-btn"
            onClick={() => setShowForm(true)}
            data-testid="clients-new"
          >
            <Plus size={16} strokeWidth={2} aria-hidden="true" />
            Nuevo cliente
          </button>
        </div>
        <p className="cli-header-subtitle">
          {isLoading
            ? ' '
            : groups.length === 0
              ? 'Aún no hay clientes registrados.'
              : `${groups.length} ${groups.length === 1 ? 'cliente' : 'clientes'} en la agenda.`}
        </p>
      </header>

      <div className="cli-toolbar">
        <div className="cli-search">
          <Search size={20} strokeWidth={2} className="cli-search-icon" aria-hidden="true" />
          <input
            type="search"
            placeholder="Buscar cliente"
            className="cli-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="clients-search"
            aria-label="Buscar cliente"
          />
          {search && (
            <button
              type="button"
              className="cli-search-clear"
              aria-label="Limpiar búsqueda"
              onClick={() => setSearch('')}
              data-testid="clients-search-clear"
            >
              <X size={16} strokeWidth={2} aria-hidden="true" />
            </button>
          )}
        </div>
        <div className="cli-sort" role="group" aria-label="Ordenar clientes">
          <span className="cli-sort-label">Ordenar:</span>
          <div className="cli-sort-segmented" data-testid="clients-sort">
            {SORT_OPTIONS.map((o) => (
              <button
                key={o.key}
                type="button"
                className={`cli-sort-segment${sort === o.key ? ' cli-sort-segment--active' : ''}`}
                aria-pressed={sort === o.key}
                onClick={() => setSort(o.key)}
                data-testid={`clients-sort-${o.key}`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {isLoading ? (
        <SkeletonList count={6} testId="clients-loading" />
      ) : filtered.length === 0 ? (
        groups.length === 0 ? (
          <EmptyState
            testId="clients-empty"
            variant="noResults"
            title="Aún no hay clientes"
            body={
              <>
                Crea un cliente con el botón "Nuevo cliente" o guárdalo desde
                un trabajo en el móvil. Los clientes se sincronizan al instante
                entre móvil y web.
              </>
            }
          />
        ) : (
          <EmptyState
            testId="clients-empty"
            variant="noResults"
            title="Sin resultados"
            body={
              <>
                Ningún cliente coincide con tu búsqueda. Prueba con otro término o
                limpia la búsqueda.
              </>
            }
          />
        )
      ) : (
        <ul className="cli-list" data-testid="clients-list">
          {filtered.map((g) => (
            <li key={g.key}>
              <Link
                to={`/clients/${g.slug}`}
                className="cli-card"
                data-testid={`clients-card-${g.slug}`}
              >
                <div className="cli-card-main">
                  <span className="cli-card-name">{g.name}</span>
                  <span className="cli-card-meta">
                    {g.total} {g.total === 1 ? 'trabajo' : 'trabajos'}
                    <span className="cli-card-meta-sep">·</span>
                    {formatRelative(g.lastUpdatedAt)}
                  </span>
                  {(g.phone || g.address) && (
                    <span className="cli-card-contact">
                      {g.phone ? (
                        <a
                          href={`tel:${g.phone}`}
                          className="cli-card-contact-link"
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`clients-card-${g.slug}-phone`}
                        >
                          {g.phone}
                        </a>
                      ) : null}
                      {g.phone && g.address ? <span className="cli-card-meta-sep">·</span> : null}
                      {g.address ? (
                        <a
                          href={`https://maps.google.com/?q=${encodeURIComponent(g.address)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="cli-card-contact-link"
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`clients-card-${g.slug}-address`}
                        >
                          {g.address}
                        </a>
                      ) : null}
                    </span>
                  )}
                </div>
                <ChevronRight size={20} strokeWidth={2} aria-hidden="true" />
              </Link>
            </li>
          ))}
        </ul>
      )}

      {showForm && (
        <NewClientModal
          identity={identity}
          onClose={() => setShowForm(false)}
          onSaved={() => setShowForm(false)}
          // Pass the existing names so the form can flag duplicates
          // (case-insensitive) before broadcasting.
          existingNames={(clients ?? []).map((c) => c.name)}
        />
      )}
    </main>
  );
}

interface NewClientModalProps {
  identity: WebIdentity;
  existingNames: ReadonlyArray<string>;
  onClose: () => void;
  onSaved: () => void;
}

function NewClientModal({ identity, existingNames, onClose, onSaved }: NewClientModalProps) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [taxId, setTaxId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('El nombre es obligatorio.');
      return;
    }
    const lower = trimmedName.toLocaleLowerCase('es');
    const dup = existingNames.some((n) => n.trim().toLocaleLowerCase('es') === lower);
    if (dup) {
      setError('Ya existe un cliente con ese nombre.');
      return;
    }
    setError(null);
    setSubmitting(true);
    const now = Date.now();
    const client: Client = {
      id: newClientId(),
      name: trimmedName,
      phone: phone.trim() || undefined,
      address: address.trim() || undefined,
      taxId: taxId.trim() || undefined,
      createdAt: now,
      updatedAt: now,
    };
    try {
      await broadcastClient(client, identity);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo guardar.');
      setSubmitting(false);
    }
  }

  return (
    <div
      className="cli-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cli-modal-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      data-testid="clients-new-modal"
    >
      <form className="cli-modal" onSubmit={handleSubmit}>
        <div className="cli-modal-header">
          <h2 id="cli-modal-title" className="cli-modal-title">Nuevo cliente</h2>
          <button
            type="button"
            className="cli-modal-close"
            onClick={onClose}
            aria-label="Cerrar"
            data-testid="clients-new-close"
          >
            <X size={20} strokeWidth={2} aria-hidden="true" />
          </button>
        </div>

        <label className="cli-modal-field">
          <span className="cli-modal-label">Nombre*</span>
          <input
            type="text"
            className="cli-modal-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            required
            maxLength={120}
            data-testid="clients-new-name"
          />
        </label>

        <label className="cli-modal-field">
          <span className="cli-modal-label">Teléfono</span>
          <input
            type="tel"
            className="cli-modal-input"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            inputMode="tel"
            maxLength={32}
            data-testid="clients-new-phone"
          />
        </label>

        <label className="cli-modal-field">
          <span className="cli-modal-label">Dirección</span>
          <input
            type="text"
            className="cli-modal-input"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            maxLength={240}
            data-testid="clients-new-address"
          />
        </label>

        <label className="cli-modal-field">
          <span className="cli-modal-label">NIF / CIF</span>
          <input
            type="text"
            className="cli-modal-input"
            value={taxId}
            onChange={(e) => setTaxId(e.target.value)}
            maxLength={20}
            data-testid="clients-new-taxid"
          />
        </label>

        {error && (
          <p className="cli-modal-error" role="alert" data-testid="clients-new-error">
            {error}
          </p>
        )}

        <div className="cli-modal-actions">
          <button
            type="button"
            className="cli-modal-btn cli-modal-btn--ghost"
            onClick={onClose}
            disabled={submitting}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="cli-modal-btn cli-modal-btn--primary"
            disabled={submitting}
            data-testid="clients-new-submit"
          >
            {submitting ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </form>
    </div>
  );
}
